import axios from "../configs/axios";
import { Webinar } from "../types/webinar.types";

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

// Webinars require a logged-in user, so we use the shared authenticated axios
// instance (it attaches the Bearer token / refresh-token headers).
export const webinarApi = {
  getAllWebinars: async (): Promise<ApiResponse<Webinar[]>> => {
    const response = await axios.get("/webinars");
    return response.data;
  },

  getWebinarById: async (id: string): Promise<ApiResponse<Webinar>> => {
    const response = await axios.get(`/webinars/${id}`);
    return response.data;
  },

  // Resolve a presigned download URL for an uploaded resource file (private S3).
  getResourceDownloadUrl: async (
    fileKey: string
  ): Promise<ApiResponse<{ downloadUrl: string }>> => {
    const response = await axios.get(`/webinars/resource-download`, {
      params: { key: fileKey },
    });
    return response.data;
  },
};

/**
 * Convert a raw video URL into an embeddable iframe src.
 * - YouTube watch/share/shorts links -> /embed/<id>
 * - Vimeo links -> player.vimeo.com/video/<id>
 * - Zoom / iframe / anything else -> returned as-is (may not embed; see canEmbed)
 */
export const toEmbedUrl = (url: string, source: string): string => {
  if (!url) return url;

  if (source === "youtube" || /youtu\.?be/.test(url)) {
    // youtu.be/<id>, youtube.com/watch?v=<id>, /shorts/<id>, /embed/<id>
    const match = url.match(
      /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
    );
    const id = match?.[1];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }

  if (source === "vimeo" || /vimeo\.com/.test(url)) {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    const id = match?.[1];
    return id ? `https://player.vimeo.com/video/${id}` : url;
  }

  // Zoom recordings and generic iframe URLs are used as-is.
  return url;
};

/**
 * Best-effort thumbnail image for a webinar.
 * - YouTube -> hqdefault image derived from the video id
 * - Vimeo/Zoom/other -> null (caller should fall back to an icon)
 */
export const getThumbnailUrl = (url: string, source: string): string | null => {
  if (source === "youtube" || /youtu\.?be/.test(url)) {
    const match = url.match(
      /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/
    );
    const id = match?.[1];
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  }
  return null;
};

/**
 * Zoom recordings frequently send X-Frame-Options and refuse to embed.
 * We still try the iframe, but the watch page also offers an "open in new tab"
 * fallback for these.
 */
export const isEmbedReliable = (source: string): boolean => {
  return source === "youtube" || source === "vimeo";
};
